import {
  ArrowUpRightIcon,
  CircleIcon,
  EraserIcon,
  HighlighterIcon,
  LucideIcon,
  MousePointer2Icon,
  PencilIcon,
  PenLineIcon,
  PentagonIcon,
  SlashIcon,
  SquareIcon,
  TypeIcon,
  WaypointsIcon,
} from 'lucide-react'
import { isMacOs } from 'react-device-detect'

// macOS shows the ⇧ glyph; other platforms spell out Shift.
const SHIFT = isMacOs ? '⇧' : 'Shift+'

export type MarkupTool =
  | 'select'
  | 'shape'
  | 'arrow'
  | 'draw'
  | 'text'
  | 'callout'
export type ShapeKind = 'rectangle' | 'circle' | 'polygon'
export type LineType = 'arrow' | 'line' | 'polyline'
export type DrawType = 'pen' | 'highlighter' | 'eraser'
// 'cloud' reserved for forward-compat (scalloped border); v1 emits solid/dashed.
export type StrokeStyle = 'solid' | 'dashed' | 'cloud'
// Shape fill pattern. v1 renders 'solid'; the rest are forward-compat (shown
// disabled in the picker until Mapbox fill-pattern rendering lands).
export type FillStyle = 'solid' | 'bricks' | 'grid' | 'diagonal' | 'dots'
// Per-end cap for arrows/lines.
export type ArrowheadType = 'none' | 'arrow' | 'circle'

interface IOption<T> {
  value: T
  icon: LucideIcon
  label: string
  shortcut?: string
  disabled?: boolean
}

export const MAIN_TOOLS: IOption<MarkupTool>[] = [
  {
    value: 'select',
    icon: MousePointer2Icon,
    label: 'Select',
    shortcut: 'Esc',
  },
  { value: 'shape', icon: SquareIcon, label: 'Shape', shortcut: 'R' },
  { value: 'arrow', icon: SlashIcon, label: 'Line', shortcut: 'L' },
  { value: 'draw', icon: PencilIcon, label: 'Draw', shortcut: 'D' },
  { value: 'text', icon: TypeIcon, label: 'Text', shortcut: 'T' },
  // Callout tool hidden until implemented — STX1-625 (Markup Editor V2):
  // https://linear.app/struxhub/issue/STX1-625/callout-tool-text-box-leader-arrow
  // Re-enable by restoring this entry and the MessageSquareIcon import.
  // { value: 'callout', icon: MessageSquareIcon, label: 'Callout', shortcut: 'C' },
]

export const SHAPE_KINDS: IOption<ShapeKind>[] = [
  { value: 'rectangle', icon: SquareIcon, label: 'Rectangle', shortcut: 'R' },
  { value: 'circle', icon: CircleIcon, label: 'Circle', shortcut: 'O' },
  { value: 'polygon', icon: PentagonIcon, label: 'Polygon', shortcut: 'P' },
]

export const LINE_TYPES: IOption<LineType>[] = [
  { value: 'line', icon: SlashIcon, label: 'Line', shortcut: 'L' },
  {
    value: 'arrow',
    icon: ArrowUpRightIcon,
    label: 'Arrow',
    shortcut: `${SHIFT}L`,
  },
  {
    value: 'polyline',
    icon: WaypointsIcon,
    label: 'Polyline',
    shortcut: `${SHIFT}P`,
  },
]

export const DRAW_TYPES: IOption<DrawType>[] = [
  { value: 'pen', icon: PenLineIcon, label: 'Pen' },
  { value: 'highlighter', icon: HighlighterIcon, label: 'Highlighter' },
  { value: 'eraser', icon: EraserIcon, label: 'Eraser' },
]

// Endpoint caps offered in the arrow/line context toolbar (separate start + end controls).
export const ARROWHEAD_TYPES: { value: ArrowheadType; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'arrow', label: 'Arrow' },
  { value: 'circle', label: 'Circle' },
]

// Fill patterns offered in the Shape color menu. Only 'solid' is selectable;
// the patterned fills are shown disabled until Mapbox fill-pattern rendering
// lands (the field still persists the choice).
export const FILL_STYLES: {
  value: FillStyle
  label: string
  disabled?: boolean
}[] = [
  { value: 'solid', label: 'Solid' },
  // Non-solid fill patterns hidden until implemented — STX1-626 (Markup Editor V2):
  // https://linear.app/struxhub/issue/STX1-626/non-solid-shape-fill-patterns-bricks-grid-diagonal-dots
  // { value: 'bricks', label: 'Bricks', disabled: true },
  // { value: 'grid', label: 'Grid', disabled: true },
  // { value: 'diagonal', label: 'Diagonal', disabled: true },
  // { value: 'dots', label: 'Dots', disabled: true },
]

/** Default stroke thickness (px) the eraser uses, per design. */
export const ERASER_THICKNESS = 72

/** Side/diameter (screen px) of a shape placed by a single click (no drag). */
export const DEFAULT_SHAPE_PX = 120
